import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { resolveImageUrl } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Repeat, DollarSign, Plus, Trash2, ImageOff, HandshakeIcon, Users, Search } from "lucide-react";
import { toast } from "sonner";

function TradeCard({ listing, currentUserId, onInterest, onDelete, onNavigate }) {
  const isOwner = listing.user_id === currentUserId;
  const have = listing.have_release || {};
  const typeBadge = listing.type === "sale" ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/20"
    : listing.type === "trade" ? "bg-blue-500/10 text-blue-400 border-blue-500/20"
    : "bg-amber-500/10 text-amber-400 border-amber-500/20";

  return (
    <div data-testid={`trade-card-${listing.trade_id}`} className="bg-zinc-900/60 backdrop-blur-xl border border-white/5 rounded-xl p-4 hover:border-zinc-700 transition-colors">
      <div className="flex gap-4">
        {have.model_image ? (
          <img src={resolveImageUrl(have.model_image)} alt="" className="w-20 h-20 rounded-lg object-cover cursor-pointer flex-shrink-0" onClick={() => onNavigate(have.release_id)} />
        ) : (
          <div className="w-20 h-20 rounded-lg bg-zinc-800 flex items-center justify-center cursor-pointer flex-shrink-0" onClick={() => onNavigate(have.release_id)}>
            <ImageOff className="w-6 h-6 text-zinc-700" />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full border ${typeBadge}`}>
              {listing.type === "both" ? "Sale/Trade" : listing.type}
            </span>
            <span className="text-[10px] text-zinc-600">by {listing.user_name}</span>
          </div>
          <p className="text-sm font-medium text-zinc-100 truncate cursor-pointer hover:text-rose-400" onClick={() => onNavigate(have.release_id)}>
            {have.release_name || listing.have_release_id}
          </p>
          <p className="text-[11px] text-zinc-500">{have.vehicle} &middot; {have.khmg_number}</p>

          {listing.price && (
            <p className="text-sm font-mono text-emerald-400 mt-1">Asking: ${listing.price}</p>
          )}

          {listing.want_releases?.length > 0 && (
            <div className="mt-2">
              <p className="text-[10px] text-zinc-500 uppercase tracking-wider mb-1">Looking for:</p>
              <div className="flex gap-1 flex-wrap">
                {listing.want_releases.map((w) => (
                  <span key={w.release_id} className="text-[10px] bg-zinc-800 text-zinc-300 px-2 py-0.5 rounded-full border border-zinc-700 cursor-pointer hover:border-zinc-500" onClick={() => onNavigate(w.release_id)}>
                    {w.release_name || w.release_id}
                  </span>
                ))}
              </div>
            </div>
          )}

          {listing.description && (
            <p className="text-xs text-zinc-500 mt-2 italic">{listing.description}</p>
          )}
        </div>
        <div className="flex flex-col gap-2 items-end flex-shrink-0">
          {listing.interest_count > 0 && (
            <span className="text-[10px] text-zinc-500 flex items-center gap-1"><Users className="w-3 h-3" />{listing.interest_count}</span>
          )}
          {!isOwner && (
            <Button data-testid={`interest-${listing.trade_id}`} size="sm" onClick={() => onInterest(listing.trade_id)} className="bg-rose-600 hover:bg-rose-500 text-white text-xs">
              <HandshakeIcon className="w-3 h-3 mr-1" /> Interested
            </Button>
          )}
          {isOwner && (
            <Button data-testid={`delete-trade-${listing.trade_id}`} size="sm" variant="ghost" onClick={() => onDelete(listing.trade_id)} className="text-zinc-500 hover:text-rose-400">
              <Trash2 className="w-3.5 h-3.5" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

export default function TradeBoardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [listings, setListings] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState({ type: "trade", have_release_id: "", want_release_ids: "", price: "", description: "" });
  const [releaseSearch, setReleaseSearch] = useState("");
  const [releaseResults, setReleaseResults] = useState([]);

  const fetchListings = useCallback(async () => {
    try {
      const params = new URLSearchParams({ status: "active", limit: "50" });
      if (typeFilter !== "all") params.set("type", typeFilter);
      const { data } = await api.get(`/trades?${params}`);
      setListings(data.listings);
      setTotal(data.total);
    } catch { toast.error("Failed to load listings"); }
    finally { setLoading(false); }
  }, [typeFilter]);

  useEffect(() => { fetchListings(); }, [fetchListings]);

  const searchReleases = async (q) => {
    setReleaseSearch(q);
    if (q.length < 2) { setReleaseResults([]); return; }
    try {
      const { data } = await api.get(`/releases?search=${encodeURIComponent(q)}&limit=8`);
      setReleaseResults(data.releases);
    } catch { setReleaseResults([]); }
  };

  const handleCreate = async () => {
    if (!createForm.have_release_id) { toast.error("Select a release you have"); return; }
    try {
      const payload = {
        type: createForm.type,
        have_release_id: createForm.have_release_id,
        want_release_ids: createForm.want_release_ids ? createForm.want_release_ids.split(",").map((s) => s.trim()).filter(Boolean) : [],
        price: createForm.price ? parseFloat(createForm.price) : null,
        description: createForm.description || null,
      };
      await api.post("/trades", payload);
      toast.success("Listing created!");
      setShowCreate(false);
      setCreateForm({ type: "trade", have_release_id: "", want_release_ids: "", price: "", description: "" });
      fetchListings();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const handleInterest = async (tradeId) => {
    try {
      const { data } = await api.post(`/trades/${tradeId}/interest`);
      toast.success(data.message);
      fetchListings();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const handleDelete = async (tradeId) => {
    try {
      await api.delete(`/trades/${tradeId}`);
      toast.success("Listing removed");
      fetchListings();
    } catch { toast.error("Failed to remove"); }
  };

  const filtered = searchQuery
    ? listings.filter((l) => {
        const q = searchQuery.toLowerCase();
        return (l.have_release?.release_name || "").toLowerCase().includes(q)
          || (l.want_releases || []).some((w) => (w.release_name || "").toLowerCase().includes(q));
      })
    : listings;

  if (loading) {
    return (
      <div className="p-4 sm:p-6 lg:p-8 space-y-4">
        {[1, 2, 3].map((i) => <div key={i} className="h-28 bg-zinc-900 rounded-xl animate-pulse" />)}
      </div>
    );
  }

  return (
    <div data-testid="trade-board-page" className="p-4 sm:p-6 lg:p-8 space-y-6 animate-fadeIn">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold text-zinc-100 tracking-tight flex items-center gap-3">
            <Repeat className="w-8 h-8 text-rose-500" /> Trade Board
          </h1>
          <p className="text-zinc-500 mt-1 text-sm">{total} active listings</p>
        </div>
        <Button data-testid="create-listing-btn" onClick={() => setShowCreate(true)} className="bg-rose-600 hover:bg-rose-500 text-white">
          <Plus className="w-4 h-4 mr-1" /> New Listing
        </Button>
      </div>

      {/* Filters */}
      <div className="flex gap-3 flex-wrap">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
          <Input data-testid="trade-search" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="Search listings..." className="pl-10 bg-zinc-900 border-zinc-800 text-zinc-100" />
        </div>
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger data-testid="type-filter" className="w-[140px] bg-zinc-900 border-zinc-800 text-zinc-300"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-zinc-900 border-zinc-800">
            <SelectItem value="all">All Types</SelectItem>
            <SelectItem value="sale">For Sale</SelectItem>
            <SelectItem value="trade">For Trade</SelectItem>
            <SelectItem value="both">Sale/Trade</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Listings */}
      {filtered.length === 0 ? (
        <div className="text-center py-20">
          <Repeat className="w-12 h-12 text-zinc-700 mx-auto mb-3" />
          <p className="text-zinc-500 text-lg">No listings yet</p>
          <p className="text-zinc-600 text-sm mt-1">Be the first to post a trade or sale</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((listing) => (
            <TradeCard key={listing.trade_id} listing={listing} currentUserId={user?.user_id} onInterest={handleInterest} onDelete={handleDelete} onNavigate={(id) => navigate(`/catalog/${id}`)} />
          ))}
        </div>
      )}

      {/* Create Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100 max-w-md">
          <DialogHeader><DialogTitle className="font-heading">Create Trade Listing</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label className="text-zinc-400 text-xs">Listing Type</Label>
              <Select value={createForm.type} onValueChange={(v) => setCreateForm({ ...createForm, type: v })}>
                <SelectTrigger className="bg-zinc-950 border-zinc-800 text-zinc-300 mt-1"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800">
                  <SelectItem value="sale">For Sale</SelectItem>
                  <SelectItem value="trade">For Trade</SelectItem>
                  <SelectItem value="both">Sale or Trade</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-zinc-400 text-xs">I Have (search release)</Label>
              <Input value={releaseSearch} onChange={(e) => searchReleases(e.target.value)} placeholder="Search by name..." className="bg-zinc-950 border-zinc-800 text-zinc-100 mt-1" />
              {releaseResults.length > 0 && (
                <div className="mt-1 bg-zinc-950 border border-zinc-800 rounded-lg max-h-40 overflow-y-auto">
                  {releaseResults.map((r) => (
                    <button key={r.release_id} onClick={() => { setCreateForm({ ...createForm, have_release_id: r.release_id }); setReleaseSearch(r.release_name); setReleaseResults([]); }} className="w-full text-left px-3 py-2 text-sm hover:bg-zinc-800 text-zinc-300 truncate">
                      {r.release_name} <span className="text-zinc-600 text-xs">{r.khmg_number}</span>
                    </button>
                  ))}
                </div>
              )}
              {createForm.have_release_id && <p className="text-[10px] text-emerald-400 mt-1">Selected: {createForm.have_release_id}</p>}
            </div>
            {(createForm.type === "sale" || createForm.type === "both") && (
              <div>
                <Label className="text-zinc-400 text-xs">Asking Price ($)</Label>
                <Input type="number" step="0.01" value={createForm.price} onChange={(e) => setCreateForm({ ...createForm, price: e.target.value })} className="bg-zinc-950 border-zinc-800 text-zinc-100 mt-1" placeholder="0.00" />
              </div>
            )}
            {(createForm.type === "trade" || createForm.type === "both") && (
              <div>
                <Label className="text-zinc-400 text-xs">Looking For (release IDs, comma separated)</Label>
                <Input value={createForm.want_release_ids} onChange={(e) => setCreateForm({ ...createForm, want_release_ids: e.target.value })} className="bg-zinc-950 border-zinc-800 text-zinc-100 mt-1" placeholder="khmg-042, khmg-118" />
              </div>
            )}
            <div>
              <Label className="text-zinc-400 text-xs">Description</Label>
              <Input value={createForm.description} onChange={(e) => setCreateForm({ ...createForm, description: e.target.value })} className="bg-zinc-950 border-zinc-800 text-zinc-100 mt-1" placeholder="Any details about condition, shipping..." />
            </div>
            <Button data-testid="confirm-create-listing" onClick={handleCreate} className="w-full bg-rose-600 hover:bg-rose-500 text-white">Post Listing</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
